import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import hash from 'object-hash';
import fetchProgress from 'fetch-progress';
import * as Sentry from '@sentry/browser';

import { currentCompanyIdSelector } from 'domain/documents/documentSelector';
import Api from 'domain/api';

import tokenKeeper from 'lib/apiTokenKeeper';
import {
  cachedPaymentSelector,
  paymentInvoicesSelector,
  paymentNavigationSelector,
  paymentsByIdSelector,
} from 'domain/payments/selectors';
import { saveCachedPaymentAction } from 'domain/payments/actions';
import { CachedPaymentFactory } from 'domain/payments/factories';

const createAuthHeader = (token: string) => ({ Authorization: `Bearer ${token || ''}` });
export const createHeaders = (token: string) => ({
  headers: new window.Headers({
    ...createAuthHeader(token),
  }),
});

let cache: Cache | null = null;

export const getStore = async (): Promise<Cache> => {
  if (!cache) {
    cache = await caches.open('payments');
  }

  // Safari does not support estimate function
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage ?? 0;
    const quota = estimate.quota ?? 1;
    const used = ((usage / quota) * 100).toFixed(2);
    if (Number(used) > 50) {
      cache.keys().then((keys: readonly Request[]) => {
        const i = Math.round(keys.length / 2);
        keys.forEach((request: Request, index: number) => {
          if (index < i) {
            (cache as Cache).delete(request);
          }
        });
      });
    }
  }
  return cache;
};

const createHash = (urlParams: Record<string, unknown>, invoices?: Array<{ referenceNumber: string }> | null) =>
  hash({ ...urlParams, invoices: invoices ? invoices.map(({ referenceNumber }) => referenceNumber) : [] });

interface UsePreloadPaymentsParams {
  currentPaymentId: string;
}

const usePreloadPayments = ({ currentPaymentId }: UsePreloadPaymentsParams) => {
  const dispatch = useDispatch();
  const isDownloadingRef = useRef(false);

  const navigation = useSelector(paymentNavigationSelector);
  const companyId = useSelector(currentCompanyIdSelector);
  const payments = useSelector(paymentsByIdSelector);
  const currentPaymentInvoices = useSelector(paymentInvoicesSelector);

  const nextPaymentId = currentPaymentId !== navigation.get('nextPaymentId') ? navigation.get('nextPaymentId') : '';
  const prevPaymentId = currentPaymentId !== navigation.get('prevPaymentId') ? navigation.get('prevPaymentId') : '';

  const getParams = useCallback(
    ({ paymentId }: { paymentId: string }) => ({
      companyId,
      paymentId,
    }),
    [companyId],
  );

  const prevPaymentUrlParams = useMemo(() => getParams({ paymentId: prevPaymentId }), [getParams, prevPaymentId]);
  const nextPaymentUrlParams = useMemo(() => getParams({ paymentId: nextPaymentId }), [getParams, nextPaymentId]);

  const currentPaymentUrlParams = useMemo(
    () =>
      getParams({
        paymentId: currentPaymentId,
      }),
    [getParams, currentPaymentId],
  );

  const currentPaymentHash = createHash(currentPaymentUrlParams, currentPaymentInvoices);
  const nextPaymentHash = createHash(nextPaymentUrlParams, payments.get(nextPaymentId)?.documentsReferenceNumbers);
  const prevPaymentHash = createHash(prevPaymentUrlParams, payments.get(prevPaymentId)?.documentsReferenceNumbers);

  const isPaymentCached = useCallback(async (url: string) => {
    const cache = await getStore();
    return cache.match(url);
  }, []);

  const currentPaymentProgress = useSelector(cachedPaymentSelector(currentPaymentHash)).downloadProgress;

  const [currentCachedPayment, setCurrentCachedPayment] = React.useState<{ blob: Blob | null; hash: string }>({ blob: null, hash: '' });
  const isCurrentPaymentCached = !!currentCachedPayment.blob && currentCachedPayment.hash === currentPaymentHash;

  const downloadPayment = useCallback(
    async ({ paymentUrl, paymentHash, token }: { paymentUrl: string; paymentHash: string; token: string }) => {
      const cache = await getStore();

      const response = await cache.match(paymentUrl);
      if (response) {
        const isInvalidCache = response.status !== 200 || paymentHash !== response.headers.get('paymentHash');
        if (isInvalidCache) {
          cache.delete(paymentUrl);
        } else {
          dispatch(
            saveCachedPaymentAction({
              data: CachedPaymentFactory({ hash: paymentHash, downloadProgress: 100 }),
            }),
          );
          return response;
        }
      }

      const res = await window.fetch(paymentUrl, createHeaders(token)).then(
        fetchProgress({
          onProgress({ total, transferred }) {
            if (transferred <= total) {
              dispatch(
                saveCachedPaymentAction({
                  data: CachedPaymentFactory({
                    hash: paymentHash,
                    downloadProgress: Math.round((transferred / total) * 100),
                  }),
                }),
              );
            } else {
              dispatch(
                saveCachedPaymentAction({
                  data: CachedPaymentFactory({ hash: paymentHash, downloadProgress: 100 }),
                }),
              );
            }
          },
        }),
      );
      const clonedRes = res.clone();
      const modifiedRes = new Response(clonedRes.body, clonedRes);
      modifiedRes.headers.set('paymentHash', paymentHash);
      // if appear error with put may be cache store full -> should remove elements
      try {
        await cache.put(paymentUrl, modifiedRes);
      } catch (e) {
        Sentry.captureMessage(String(e));
      }

      return res;
    },
    [dispatch],
  );

  useEffect(() => {
    const init = async () => {
      const token = (await tokenKeeper.getToken()) as string;
      if (isCurrentPaymentCached && !isDownloadingRef.current) {
        const [nextPaymentUrl, prevPaymentUrl] = [nextPaymentUrlParams, prevPaymentUrlParams].map(Api.getPaymentUrl);

        if (nextPaymentId && token && !(await isPaymentCached(nextPaymentUrl))) {
          await downloadPayment({ paymentUrl: nextPaymentUrl, token, paymentHash: nextPaymentHash });
        }
        if (prevPaymentId && token && !(await isPaymentCached(prevPaymentUrl))) {
          await downloadPayment({ paymentUrl: prevPaymentUrl, token, paymentHash: prevPaymentHash });
        }
      } else if (token && !isDownloadingRef.current) {
        isDownloadingRef.current = true;
        const paymentUrl = Api.getPaymentUrl(currentPaymentUrlParams);
        downloadPayment({ paymentUrl, paymentHash: currentPaymentHash, token })
          .then((res) => res.blob())
          .then((file) => {
            setCurrentCachedPayment({ blob: file, hash: currentPaymentHash });
            isDownloadingRef.current = false;
          });
      }
    };
    if (companyId) {
      init();
    }
  }, [
    nextPaymentId,
    prevPaymentId,
    downloadPayment,
    isCurrentPaymentCached,
    currentPaymentUrlParams,
    nextPaymentUrlParams,
    prevPaymentUrlParams,
    nextPaymentHash,
    prevPaymentHash,
    currentPaymentHash,
    isPaymentCached,
    companyId,
  ]);
  return { blob: currentCachedPayment.blob, downloadProgress: currentPaymentProgress };
};

export default usePreloadPayments;
